import { DEFAULT_INVOICE_FORM_VALUES, type InvoiceDocumentFieldGroup } from "./constants"
import { InvoiceRowAction } from "./constants/rowActions"
import type { InvoiceAttachmentFormValues, InvoiceFormValues } from "./schemas/invoiceForm"
import type { InvoiceFormDialogMode } from "./types/dialog"
import { isApproveSyncFailed } from "./utils/invoiceAs400Sync"

import type { CreateInvoiceInput, Invoice, UpdateInvoiceInput } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, InvoiceStatus, InvoiceType } from "@/graphql/codegen/graphql"

export const isInvoiceDocumentFieldGroup = (
  value: FieldGroupEnum | null | undefined
): value is InvoiceDocumentFieldGroup =>
  value === FieldGroupEnum.InvoiceInvoice || value === FieldGroupEnum.InvoiceOther

export const buildDuplicateWarningDialogMessage = (count: number): string => {
  const subject = count === 1 ? "is 1 existing invoice" : `are ${count} existing invoices`
  return `There ${subject} with similar details. This could mean you're about to enter a duplicate, or the vendor issued the same invoice more than once.`
}

export const calculateInvoiceExpenseTotal = (invoice: Pick<Invoice, "expenseLines">): number =>
  invoice.expenseLines.reduce((sum, line) => sum + line.amount, 0)

export const toInvoiceFormValues = (invoice?: Invoice): InvoiceFormValues => {
  if (!invoice) {
    return DEFAULT_INVOICE_FORM_VALUES
  }

  return {
    vendorId: invoice.vendorCompanyId,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate ?? "",
    dueDate: invoice.dueDate,
    totalAmount: calculateInvoiceExpenseTotal(invoice),
    printedCheck: invoice.printedCheck,
    expenseLines: invoice.expenseLines.map((line) => ({
      expenseAccountId: line.glAccountId,
      amount: line.amount,
    })),
    // Seed empty: this form field represents NEW uploads only. Existing invoice attachments
    // are uploaded directly against the invoice (referenceTableName=INVOICE) and surfaced
    // via a separate documents query.
    attachments: [] as InvoiceAttachmentFormValues[],
    comment: "",
  }
}

interface ToInvoiceCreateInputParams {
  formValues: InvoiceFormValues
  duplicateWarningBypassed?: boolean
}

// Callers must only invoke after schema validation has passed — `null` values are
// rejected by the schema so the non-null assertions are safe here.
export const toInvoiceCreateInput = ({
  formValues,
  duplicateWarningBypassed,
}: ToInvoiceCreateInputParams): CreateInvoiceInput => {
  return {
    vendorCompanyId: formValues.vendorId,
    invoiceNumber: formValues.invoiceNumber,
    invoiceDate: formValues.invoiceDate,
    dueDate: formValues.dueDate,
    invoiceType: InvoiceType.General,
    printedCheck: formValues.printedCheck,
    duplicateWarningBypassed,
    // Deliberately not sent through CreateInvoiceInput: the BE handler writes a Comment row
    // when `input.comment` is non-empty, and useInvoiceForm already chains an explicit
    // CommentMutation.create after invoice.create (the sole writer to the Comment table that
    // CommentListQuery — i.e. the expanded-row Comments pane — reads from). Sending it here
    // would persist the comment twice.
    comment: undefined,
    expenseLines: formValues.expenseLines.map((line) => ({
      glAccountId: line.expenseAccountId,
      amount: line.amount!,
    })),
  }
}

interface ToInvoiceUpdateInputParams {
  formValues: InvoiceFormValues
  invoiceId: string
  duplicateWarningBypassed?: boolean
}

// Callers must only invoke after schema validation has passed — `null` values are
// rejected by the edit-mode schema so the non-null assertions are safe here.
export const toInvoiceUpdateInput = ({
  formValues,
  invoiceId,
  duplicateWarningBypassed,
}: ToInvoiceUpdateInputParams): UpdateInvoiceInput => {
  return {
    invoiceId,
    vendorCompanyId: formValues.vendorId,
    invoiceNumber: formValues.invoiceNumber,
    invoiceDate: formValues.invoiceDate,
    dueDate: formValues.dueDate,
    printedCheck: formValues.printedCheck,
    // Schema guarantees a non-empty comment in edit modes.
    comment: formValues.comment ?? "",
    duplicateWarningBypassed,
    expenseLines: formValues.expenseLines.map((line) => ({
      glAccountId: line.expenseAccountId,
      amount: line.amount!,
    })),
  }
}

// Derives the InvoiceFormDialog mode from an existing invoice's status + the row action chosen.
// AK-5232 AC3: an approved invoice whose voucher never reached AS400 can be resubmitted, like a denied one.
export const resolveInvoiceFormDialogMode = (
  invoice: Invoice,
  rowAction: InvoiceRowAction.Edit | InvoiceRowAction.View
): InvoiceFormDialogMode => {
  if (rowAction === InvoiceRowAction.View) return "view"
  if (invoice.status === InvoiceStatus.NotApproved || isApproveSyncFailed(invoice)) return "edit-resubmittable"
  return "edit"
}

// Simple USD formatter for the table Amount column. Returns `$1,234.56`.
// TODO(consolidate): promote to @/utils/format when the second caller appears.
export const formatInvoiceCurrency = (value: number): string =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
